import { Theme, useTheme } from '@emotion/react';
import { ClinicalImpressionAssessmentValueCode } from '@quromedical/fhir-common';
import { MeasurementName, Note } from '@quromedical/models';
import { NEWS2ClinicalRisk, Scales } from '@quromedical/news';
import { first, last } from '@quromedical/utils';
import { upperfirst } from '@quromedical/utils';
import { getAggregatedRanges } from '@quromedical/vital-metrics';
import { StopProps } from 'components/graph';
import * as Graph from 'components/graph';
import { news2RiskColorMap } from 'core/vitals';
import { StatusLineData, TimelineCard, TimelinePoint } from 'design-system/components';
import { useResponsive } from 'hooks/useResponsive';
import { VitalsApi } from 'integration/aggregate';
import React, { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

import { codeAssessment } from './record';

interface NoteSummaryGraphProps {
  notes: Note[];
  vitals: VitalsApi;
  id: string;
}

interface Measurement {
  ts: number;
  value: string;
  type: MeasurementName;
}

const isValidPoint = (point: Partial<TimelinePoint>): point is TimelinePoint => {
  const hasY = typeof point.y !== 'undefined';
  const hasData = hasY && point.x && point.text;

  return !!hasData;
};

enum AssessmentCodeValue {
  Deteriorating = -1,
  Stable = 0,
  Improving = 1,
}

const margin: Graph.Margin = {
  ...Graph.defaultMargin,
  // need some space on the left so that the text doesn't overlap
  left: 84,
};

const assessmentCodeValueMap: Partial<
  Record<ClinicalImpressionAssessmentValueCode, Partial<AssessmentCodeValue>>
> = {
  [ClinicalImpressionAssessmentValueCode.Deteriorating]: AssessmentCodeValue.Deteriorating,
  [ClinicalImpressionAssessmentValueCode.Stable]: AssessmentCodeValue.Stable,
  [ClinicalImpressionAssessmentValueCode.Improving]: AssessmentCodeValue.Improving,
};

const assessmentCodeValueDisplayMap: Record<Partial<AssessmentCodeValue>, string> = {
  [AssessmentCodeValue.Deteriorating]:
    codeAssessment[ClinicalImpressionAssessmentValueCode.Deteriorating],
  [AssessmentCodeValue.Stable]: codeAssessment[ClinicalImpressionAssessmentValueCode.Stable],
  [AssessmentCodeValue.Improving]: codeAssessment[ClinicalImpressionAssessmentValueCode.Improving],
};

const createStops = (theme: Theme): StopProps[] => [
  {
    offsetVal: 1,
    color: theme.color['status-success'],
  },
  {
    offsetVal: 0,
    color: theme.color['accent-blue'],
  },
  {
    offsetVal: -1,
    color: theme.color['status-critical'],
  },
];

const riskScoreDisplay: Record<NEWS2ClinicalRisk, string> = {
  low: '1-4',
  'low-medium': '1-4',
  medium: '5-6',
  high: '>7',
};

const assessmentCodeFormatter = (value: number): string =>
  assessmentCodeValueDisplayMap[value as AssessmentCodeValue] || '';

export const NoteSummaryGraph: React.FC<NoteSummaryGraphProps> = ({ notes, id, vitals }) => {
  const theme = useTheme();
  const aspectRatio = useResponsive({ sm: 3, lg: 4, xl: 5, xxl: 6 }, 3);

  const points: TimelinePoint[] = notes
    .map<Partial<TimelinePoint>>(item => {
      const { note } = item;
      const x = item.dateCreated ? new Date(item.dateCreated).getTime() : undefined;

      const y = note.assessmentCode ? assessmentCodeValueMap[note.assessmentCode] : undefined;

      return {
        y,
        x,
        text: first(note.subjective),
      };
    })
    .filter(isValidPoint);

  const start: Note | undefined = last(notes);

  const starts = useMemo(
    () => (start?.dateCreated ? new Date(start?.dateCreated) : new Date()),
    [start]
  );

  const stops = createStops(theme);

  const resolvedTimes = useCallback(
    () => ({
      end: Date.now(),
      start: starts.getTime(),
    }),
    [starts]
  )();

  const fetcher = useCallback(
    () =>
      vitals.getTracking({
        patientId: id,
        start: resolvedTimes.start,
        end: resolvedTimes.end,
      }),
    [id, resolvedTimes, vitals]
  );
  const swrKey = `vitals-tracking/${starts}`;

  const { data } = useSWR(swrKey, fetcher);
  const vitalsTracking = data?.vitals.type === 'success' ? data.vitals.data : {};

  const ewsData = vitalsTracking.EWS;

  const getEWSScore = ewsData
    ?.map<Partial<TimelinePoint>>(item => {
      const { ts, value } = item;
      const x = ts ? new Date(ts).getTime() : undefined;
      const y = value ? value / 21 : 0;
      const scaleName = Scales.news2RiskScale(y);

      return {
        y,
        x,
        text: scaleName.toUpperCase(),
      };
    })
    .filter(isValidPoint);

  const ewsScore = getEWSScore ? getEWSScore : [];

  const mapEWSToMeasurement = ewsData?.map<Measurement>(item => {
    const { measurementName, ts, value } = item;
    return {
      ts,
      value: `${value}`,
      type: measurementName,
    };
  });

  const ewsMeasurement = mapEWSToMeasurement ? mapEWSToMeasurement : [];

  const millisecondsInADay = 86400000;
  const ewsAggregatedRang = getAggregatedRanges(millisecondsInADay, ewsMeasurement);

  const ewsStatusLinesFilteredData = ewsAggregatedRang.filter(z => z.measurements.length !== 0);

  const ewsStatusLinesData = ewsStatusLinesFilteredData.map<StatusLineData>(
    (status, index, array) => {
      const { max } = status;
      const maxScore = max?.value ? +max.value : 0;
      const scaleName = Scales.news2RiskScale(maxScore);

      const score = riskScoreDisplay[scaleName];
      if (index > 0) {
        const previousEnd = array[index - 1].end;
        return {
          ...status,
          start: previousEnd,
          text: `${upperfirst(scaleName)}(${score})`,
          color: news2RiskColorMap[scaleName],
        };
      }
      return {
        ...status,
        text: `${upperfirst(scaleName)}(${score})`,
        color: news2RiskColorMap[scaleName],
      };
    }
  );

  return (
    <>
      <TimelineCard
        lines={[
          {
            points,
            color: 'accent-blue',
            showScatter: true,
            stops,
          },
          {
            points: ewsScore,
            color: 'accent-green',
            showScatter: false,
            stops,
            hideTimeline: true,
          },
        ]}
        alwaysExpanded
        margin={margin}
        blockColor="accent-blue"
        icon="sticky-note-2"
        title={strings.NoteHistoryTitle}
        valueFormatter={assessmentCodeFormatter}
        yTicks={[-1, 0, 1]}
        yDomain={[-1.5, 1]}
        timeFormat="date"
        aspectRatio={aspectRatio}
        statusLineYValue={-1.5}
        statusLines={ewsStatusLinesData}
      />
    </>
  );
};
